/** @jsxImportSource @emotion/react */
import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { useDataTableContext } from "../../store/DataTableProvider";
import { QuickActionInsertRow } from "./QuickActionInsertRow";
import { QuickActionGoToTaskInfo } from "./QuickActionGoToTaskInfo";
import { QuickActionGoToResults } from "./QuickActionGoToResults";
import { QuickActionJumpToColumn } from "./QuickActionJumpToColumn";
import { QuickActionRunSelectedRows } from "./QuickActionRunRow";
import { QuickActionDeleteSelectedRows } from "./QuickActionDeleteRow";
import { css } from "@emotion/react";
import { QuickActionAddColumn } from "./QuickActionAddColumns";
import { QuickActionInsertColumnsFromTable } from "./QuickActionInsertColumnsFromTable";
import { QuickActionInsertNewAnalysisTable } from "./QuickActionInsertNewAnalysis";
import { QuickActionToggleNotes } from "./QuickActionToggleNotes";
import { GridApi } from "ag-grid-community";
import { DataTableRowData } from "../DataTable";
import { QuickActionCopySelectedRows } from "./QuickActionCopyRow";

const styles = {
  buttonGroup: css`
    margin-right: 2px;
    padding-right: 2px;
    display: inline-block;
    border-right: 1px solid #eee;
  `,
};

const ButtonGroup = ({
  style,
  children,
}: {
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => (
  <div css={styles.buttonGroup} style={style}>
    {children}
  </div>
);

export interface DataTableQuickActionBarProps {
  hasNotes?: boolean;
  selectedRowIds: string[];
  deselectAllRows: () => void;
  gridApi: GridApi<DataTableRowData> | undefined;
  tableId: string;
}

/**
 * Component that renders groups of buttons that manipulate a data table through
 * single-click actions
 */
export const DataTableQuickActionBar = ({
  hasNotes = true,
  selectedRowIds,
  gridApi,
  tableId,
  deselectAllRows,
}: DataTableQuickActionBarProps) => {
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find(({ id }) => id === tableId);
  });

  return (
    <EuiFlexGroup responsive={false} alignItems="center">
      <EuiFlexItem>
        <div
          // don't wrap quick action buttons
          css={css`
            white-space: nowrap;
          `}
        >
          <ButtonGroup
            style={
              // match group width to display over ID column
              selectedTable?.kind === "data"
                ? {
                    width: "74px",
                  }
                : undefined
            }
          >
            <QuickActionInsertRow tableId={tableId} />
            {selectedTable?.kind === "analysis" && (
              <QuickActionCopySelectedRows
                deselectAll={deselectAllRows}
                selectedRowIds={selectedRowIds}
                tableId={tableId}
              />
            )}
            <QuickActionDeleteSelectedRows
              selectedRowIds={selectedRowIds}
              tableId={tableId}
            />
          </ButtonGroup>
          <ButtonGroup>
            {selectedTable?.kind === "analysis" && (
              <QuickActionRunSelectedRows
                tableId={tableId}
                gridApi={gridApi}
                selectedRowIds={selectedRowIds}
              />
            )}
          </ButtonGroup>
          <ButtonGroup>
            {selectedTable?.kind === "data" && (
              <QuickActionAddColumn tableId={tableId} />
            )}
            {selectedTable?.kind === "data" && (
              <QuickActionInsertColumnsFromTable tableId={tableId} />
            )}
            {selectedTable?.kind === "data" && (
              <QuickActionInsertNewAnalysisTable tableId={tableId} />
            )}
          </ButtonGroup>
        </div>
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <EuiFlexGroup responsive={false}>
          <EuiFlexItem grow={false}>
            <QuickActionJumpToColumn gridApi={gridApi} tableId={tableId} />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiFlexGroup
          responsive={false}
          gutterSize="s"
          justifyContent="flexEnd"
        >
          {selectedTable?.kind === "analysis" && (
            <EuiFlexItem grow={false}>
              <QuickActionGoToResults tableId={tableId} gridApi={gridApi} />
            </EuiFlexItem>
          )}

          {selectedTable?.kind === "analysis" && (
            <EuiFlexItem grow={false}>
              <QuickActionGoToTaskInfo gridApi={gridApi} />
            </EuiFlexItem>
          )}

          {hasNotes && (
            <EuiFlexItem grow={false}>
              <QuickActionToggleNotes />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
